import React, { useCallback, useEffect, useState } from "react";
import { Heart, Sparkles, RefreshCw, Trophy } from "lucide-react";
import { fetchWall, submitWallEntry } from "@/lib/wallService";
import { toast } from "sonner";

const relTime = (iso) => {
  try {
    const then = new Date(iso).getTime();
    const s = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (s < 60) return `hace ${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `hace ${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `hace ${h}h`;
    const d = Math.round(h / 24);
    return `hace ${d}d`;
  } catch { return "—"; }
};

/**
 * SupportWall — public opt-in list of latest donors.
 * Only nick + amount are ever exposed. Wallet addresses stay private.
 */
export const SupportWall = ({ compact = false, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const load = useCallback(async () => {
    setReloading(true);
    try {
      const d = await fetchWall(compact ? 6 : 25);
      setData(d);
    } catch {
      /* silent — wall is decorative */
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, [compact]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const entries = data?.entries || [];

  return (
    <div data-testid={compact ? "support-wall-compact" : "support-wall"}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF] inline-flex items-center gap-1.5">
          <Trophy size={12} /> Muro de apoyo
        </div>
        <button
          data-testid="support-wall-refresh"
          onClick={load}
          className="text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 inline-flex items-center gap-1"
        >
          <RefreshCw size={11} className={reloading ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      {data && (
        <div
          className="mb-2 rounded-xl px-3 py-2 flex items-center justify-between text-[11px]"
          style={{ background: "rgba(91,46,255,0.10)", border: "1px solid rgba(138,107,255,0.25)" }}
        >
          <div className="text-white/70">
            <span className="text-white font-semibold">{data.total}</span> apoyos ·{" "}
            <span className="text-white font-semibold">{data.total_bnb.toFixed(3)}</span> BNB recibidos
          </div>
          <Sparkles size={12} className="text-[#8A6BFF]" />
        </div>
      )}

      {loading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl h-10 bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div
          data-testid="wall-empty"
          className="rounded-xl px-3 py-4 text-center text-[11px] text-white/60"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(138,107,255,0.25)" }}
        >
          Nadie ha aparecido en el muro todavía. Podrías ser tú.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {entries.map((e, i) => (
            <li
              key={`${e.nick}-${e.created_at}-${i}`}
              data-testid={`wall-entry-${i}`}
              className="rounded-xl px-3 py-2 flex items-center gap-3"
              style={{
                background: i === 0 ? "rgba(214,0,47,0.10)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${i === 0 ? "rgba(214,0,47,0.35)" : "rgba(255,255,255,0.06)"}`,
              }}
            >
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{
                  background: i === 0
                    ? "linear-gradient(180deg,#D6002F,#7c001a)"
                    : "rgba(91,46,255,0.25)",
                  color: "white",
                }}
              >
                {i === 0 ? <Heart size={12} fill="currentColor" /> : `#${i + 1}`}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{e.nick}</div>
                <div className="text-[10px] text-white/45">{relTime(e.created_at)}</div>
              </div>
              <div className="text-sm font-bold text-[#8A6BFF] tabular-nums shrink-0">
                {e.amount_bnb.toFixed(3)} BNB
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * OptInForm — after a successful donation, invite the user to
 * appear on the public wall with just a nick. Fully optional.
 */
export const OptInForm = ({ txHash, onDone }) => {
  const [nick, setNick] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = nick.trim();
    if (!clean || clean.length < 2) return;
    setBusy(true);
    try {
      await submitWallEntry(clean, txHash);
      setSubmitted(true);
      toast.success("¡Estás en el muro!");
      onDone?.();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = typeof raw === "string" && raw
        ? raw
        : status === 409
        ? "Esta donación ya aparece en el muro"
        : status === 422 || status === 502
        ? "No se pudo verificar la transacción todavía. Prueba en unos segundos."
        : "No se pudo publicar";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="wall-optin-done"
        className="rounded-2xl p-3 text-center"
        style={{ background: "rgba(91,46,255,0.14)", border: "1px solid rgba(138,107,255,0.35)" }}
      >
        <Trophy size={16} className="mx-auto text-[#8A6BFF]" />
        <div className="mt-1 text-sm font-semibold text-white">Añadido al muro</div>
        <div className="text-[11px] text-white/70">Gracias por hacerlo público.</div>
      </div>
    );
  }

  return (
    <form
      data-testid="wall-optin"
      onSubmit={submit}
      className="rounded-2xl p-3"
      style={{ background: "rgba(11,11,18,0.55)", border: "1px solid rgba(138,107,255,0.28)" }}
    >
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF] mb-1">Opcional</div>
      <div className="text-sm text-white/90 mb-2">Aparece en el muro público con un nick.</div>
      <div className="flex items-stretch gap-2">
        <input
          data-testid="wall-optin-nick"
          value={nick}
          onChange={(e) => setNick(e.target.value)}
          maxLength={20}
          placeholder="tu-nick"
          className="flex-1 rounded-xl bg-white/[0.05] border border-white/10 focus:border-[#8A6BFF]/60 focus:outline-none px-3 py-2 text-sm placeholder:text-white/35"
        />
        <button
          type="submit"
          data-testid="wall-optin-submit"
          disabled={busy || nick.trim().length < 2}
          className="creep-btn text-xs !py-1.5 !px-3 disabled:opacity-40"
        >
          {busy ? "Publicando…" : "Publicar"}
        </button>
      </div>
      <div className="mt-1 text-[10px] text-white/45">
        Solo se muestra el nick y la cantidad. Nunca tu dirección de wallet.
      </div>
    </form>
  );
};

export default SupportWall;
